import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { TextField } from '@mui/material';
import { trpc } from '@lib/trpc';
import { Button } from '@atoms';
import type { StoreRecord } from '@types';
import toast from 'react-hot-toast';

interface AdminStoreModalProps {
  store: StoreRecord | null;
  isEditing: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const AdminStoreModal: React.FC<AdminStoreModalProps> = ({
  store,
  isEditing,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load existing store data when editing
  useEffect(() => {
    if (isEditing && store) {
      setFormData({
        name: store.name,
      });
    } else {
      setFormData({
        name: '',
      });
    }
  }, [isEditing, store]);

  const createStoreMutation = trpc.store.create.useMutation({
    onSuccess: () => {
      toast.success('Store created successfully');
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create store');
    },
  });

  const updateStoreMutation = trpc.store.update.useMutation({
    onSuccess: () => {
      toast.success('Store updated successfully');
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update store');
    },
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Store name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Store name must be at least 2 characters long';
    } else if (formData.name.trim().length > 255) {
      newErrors.name = 'Store name must be less than 255 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      name: formData.name.trim(),
    };

    if (isEditing && store) {
      await updateStoreMutation.mutateAsync({
        id: store.id,
        name: submitData.name,
      });
    } else {
      await createStoreMutation.mutateAsync(submitData);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const isLoading = createStoreMutation.isPending || updateStoreMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-background-primary rounded-lg shadow-xl w-full max-w-md">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border-primary">
            <h2 className="text-xl font-semibold text-text-primary">
              {isEditing ? 'Edit Store' : 'Create Store'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-text-secondary hover:text-text-primary transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form Content */}
          <div className="p-6">
            {/* Store Name */}
            <TextField
              label="Store Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Brisbane Store, Sydney Warehouse, Melbourne Office"
              disabled={isLoading}
              error={!!errors.name}
              helperText={errors.name || 'Enter a descriptive name for the store location'}
              fullWidth
              required
              autoFocus
              variant="outlined"
              size="medium"
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border-primary">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
              className="flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>{isEditing ? 'Update' : 'Create'} Store</span>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
